import * as _ from 'lodash';
import * as React from 'react';
import { styled } from '../../theme';
import { Input, InputWrapperWidget, OTPInput } from '../inputs';
import { Btn, ButtonType, ButtonWidget } from '../buttons/ButtonWidget';

export type BaseOTPAuthenticationWidgetProps = {
  className?: string;
  disabled?: boolean;
  error?: string;
  enableBackupCodes?: boolean;
  alternativeActions?: Btn[];
  messageContent?: () => JSX.Element;
  onOTPComplete?: (code: string, type: OTPAuthenticationType) => void;
  otpLength?: number;
};

export type OTPAuthenticationWidgetProps = {
  otpValues: string[];
  onChange: (code: string, type: OTPAuthenticationType) => void;
} & BaseOTPAuthenticationWidgetProps;

export const DEFAULT_OTP_LENGTH = 6;

namespace S {
  export const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
  `;

  export const MessageContainer = styled(Container)`
    margin-top: 30px;
    max-width: 300px;
  `;

  export const ButtonContainer = MessageContainer;

  export const ActionButton = styled(ButtonWidget)`
    margin-bottom: 10px;
    min-width: 200px;
  `;

  export const MessageText = styled.div`
    font-size: 14px;
    color: ${(p) => p.theme.text.secondary};
    text-align: center;
  `;
}

export enum OTPAuthenticationType {
  PIN = 'pin',
  BACKUP_CODE = 'backup_code'
}

export const OTPAuthenticationWidget: React.FC<OTPAuthenticationWidgetProps> = (props) => {
  const {
    className,
    disabled,
    enableBackupCodes,
    error,
    messageContent,
    onChange,
    onOTPComplete,
    otpLength: specifiedOTPLength,
    otpValues
  } = props;

  const [authType, setAuthType] = React.useState<OTPAuthenticationType>(OTPAuthenticationType.PIN);

  const finalOTPLength = specifiedOTPLength ?? DEFAULT_OTP_LENGTH;

  const wrappedOnOTPChange = React.useCallback(
    (code: string, type: OTPAuthenticationType) => {
      onChange(code, type);

      if (code.length == finalOTPLength) {
        onOTPComplete?.(code, type);
      }
    },
    [onChange]
  );

  switch (authType) {
    case OTPAuthenticationType.PIN:
      return (
        <S.Container className={className}>
          <InputWrapperWidget error={error}>
            <OTPInput
              otpValues={otpValues}
              length={finalOTPLength}
              onChange={(otp) => {
                wrappedOnOTPChange(otp.join(''), authType);
              }}
              autoFocus={true}
              disabled={disabled}
              error={!!error}
            />
          </InputWrapperWidget>
          {messageContent ? <S.MessageContainer>{messageContent?.()}</S.MessageContainer> : null}
          {enableBackupCodes && (
            <S.ButtonContainer>
              {_.map(props.alternativeActions, (button, index) => (
                <S.ActionButton key={index} type={ButtonType.SECONDARY} {...button} />
              ))}
              <S.ActionButton
                label="Use backup code"
                type={ButtonType.SECONDARY}
                action={() => {
                  setAuthType(OTPAuthenticationType.BACKUP_CODE);
                  onChange('', OTPAuthenticationType.BACKUP_CODE);
                }}
              />
            </S.ButtonContainer>
          )}
        </S.Container>
      );
    case OTPAuthenticationType.BACKUP_CODE:
      return (
        <S.Container className={className}>
          <InputWrapperWidget error={error}>
            <Input
              value={props.otpValues.join('')}
              onChange={(event) => {
                onChange(event.target.value, authType);
              }}
            />
            <S.MessageContainer>
              <S.MessageText>Enter one of your stored backup codes to authenticate</S.MessageText>
            </S.MessageContainer>
            <S.ButtonContainer>
              <ButtonWidget
                label="Back to MFA code"
                type={ButtonType.SECONDARY}
                action={() => {
                  setAuthType(OTPAuthenticationType.PIN);
                  onChange('', OTPAuthenticationType.PIN);
                }}
              />
            </S.ButtonContainer>
          </InputWrapperWidget>
        </S.Container>
      );
  }
};
